import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, Target, Shield, TrendingUp, Eye, Heart, MessageCircle, Phone, Download, FileText } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import ContactForm from '@/components/ContactForm';

const CORPORATE_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/tqyuck93_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2008_53_46%20AM.png";

const benefits = [
  {
    icon: Target,
    title: 'Meaningful Social Impact',
    description: 'Your investment directly supports rider safety, training, and economic empowerment programs.',
  },
  {
    icon: Shield,
    title: 'Road Safety Contribution',
    description: 'Help reduce road accidents by funding proper training and safety equipment for riders.',
  },
  {
    icon: Eye,
    title: 'Brand Visibility',
    description: 'Your brand reaches millions through delivery bikes traveling across urban South Africa.',
  },
  {
    icon: TrendingUp,
    title: 'Impact Reporting',
    description: 'Receive quarterly reports showing exactly how your investment is making a difference.',
  },
];

const partnershipTiers = [
  {
    name: 'Bronze Partner',
    description: 'Entry-level partnership for companies starting their CSI journey.',
    features: ['Logo on website', 'Annual impact report', 'Certificate of partnership'],
  },
  {
    name: 'Silver Partner',
    description: 'Enhanced visibility and deeper engagement with the movement.',
    features: ['Everything in Bronze', 'Bike branding (5 bikes)', 'Quarterly reports', 'Social media mentions'],
  },
  {
    name: 'Gold Partner',
    description: 'Strategic partnership with maximum impact and visibility.',
    features: ['Everything in Silver', 'Bike branding (20 bikes)', 'Featured case study', 'Event co-hosting', 'Direct rider engagement'],
  },
];

const CorporateCSIPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="corporate-csi-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-slate-50 to-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase">
                  Corporate CSI Partnerships
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900">
                  Partner With <span className="text-primary-mlm">Purpose</span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  Join leading South African corporates in supporting rider safety and economic 
                  empowerment. Your CSI investment creates real, measurable social impact.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    asChild
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold shadow-lg"
                    data-testid="csi-contact-btn"
                  >
                    <a href="#enquiry-form">
                      Enquire Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 font-semibold"
                  >
                    <a href="https://wa.me/27720286584" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 w-5 h-5" />
                      WhatsApp Us
                    </a>
                  </Button>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <img
                  src={CORPORATE_IMAGE}
                  alt="Corporate Partnership"
                  className="rounded-3xl shadow-xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-primary-mlm rounded-2xl p-6 shadow-xl text-white">
                  <Building2 className="w-8 h-8 mb-2" />
                  <p className="text-lg font-semibold">CSI Excellence</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Why Partner With #MyLifeMatters?
              </h2>
              <p className="text-lg text-slate-600">
                Your CSI investment goes beyond compliance — it creates real change in the 
                lives of delivery riders and their families.
              </p>
            </div>
          </FadeIn>

          {/* Gig Fleet Academy Logo */}
          <FadeIn delay={0.1}>
            <div className="flex justify-center mb-16">
              <img 
                src="https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/9gzgulm0_image.png"
                alt="Gig Fleet Academy"
                className="h-32 md:h-40 w-auto"
              />
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <FadeIn key={benefit.title} delay={0.1 * index}>
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-primary-mlm" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Download Partnership Brochure */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
        <div className="container-wide">
          <FadeIn>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 max-w-4xl mx-auto">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center shrink-0">
                  <FileText className="w-7 h-7 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-2">
                    Download Our Partnership Brochure
                  </h3>
                  <p className="text-purple-200 text-sm md:text-base">
                    Get the full details on how your organisation can partner with #MyLifeMatters 
                    to create measurable social impact through rider safety and empowerment.
                  </p>
                </div>
              </div>
              <a
                href="/MyLifeMatters_Call_For_Partnership.pdf"
                download
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-slate-900 rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
                data-testid="csi-download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" className="py-20 md:py-32 bg-white scroll-mt-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase">
                  Get In Touch
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900">
                  Start Your Partnership Journey
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Fill out the form and our team will be in touch within 24 hours to discuss 
                  how we can create meaningful impact together.
                </p>
                <div className="pt-6 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary-mlm" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Call us directly</p>
                      <a href="tel:+27720286584" className="text-lg font-semibold text-slate-900 hover:text-primary-mlm">
                        072 028 6584
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-primary-mlm" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">WhatsApp us</p>
                      <a href="https://wa.me/27720286584" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-slate-900 hover:text-primary-mlm">
                        Chat Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="bg-slate-50 rounded-2xl p-8">
                <ContactForm formType="corporate" showCompany={true} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateCSIPage;
